import { __assign, __awaiter, __generator, __read } from "tslib";
import { mergeClasses } from '@fluentui/react-components';
import { fetchAIHubEnabled } from '@office-start/ai-hub-config-service';
import { AppBar, FlyoutTileID, } from '@office-start/appbar-control';
import { HubTileID } from '@office-start/appbar-types';
import { useFeatureGates } from '@office-start/feature-gates-service';
import { NovaContextProvider } from '@office-start/nova-context-provider';
import OfficeFluentProvider from '@office-start/office-fluent-provider';
import { useRemotableRenderer } from '@office-start/remote-module-utility';
import { useRegisterTeachingUiTargetElement } from '@office-start/teaching-ui-service';
import { useIsDarkMode } from '@office-start/theming-service';
import classNames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import styles from './AppBarComponent.module.scss';
import { LazyFeedbackComponent } from './LazyComponents/LazyFeedbackComponent';
import { LazyMeControlComponent } from './LazyComponents/LazyMeControlComponent';
import { M365Logo } from './M365Logo';
export var AppBarComponent = function (_a) {
    var _b, _c, _d, _e;
    var appbarConnectorService = _a.appbarConnectorService, allowRemote = _a.allowRemote;
    var registerAppBarAppsCallout = useRegisterTeachingUiTargetElement('AppBar_AppsButton');
    var registerAppBarMyContentCallout = useRegisterTeachingUiTargetElement('AppBar_MyContent');
    var registerAppBarCreateCallout = useRegisterTeachingUiTargetElement('AppBar_CreateCenter');
    var registerFeedCallout = useRegisterTeachingUiTargetElement('AppBar_Feed');
    var registerFlyoutCallout = useRegisterTeachingUiTargetElement('AppBar_Flyout');
    var registerAppBarOneDriveCallout = useRegisterTeachingUiTargetElement('AppBar_OneDrive');
    var appBarRefCallback = useCallback(function (instance) {
        var _a, _b, _c, _d, _e, _f;
        if (instance) {
            // Update the functions in the service
            appbarConnectorService === null || appbarConnectorService === void 0 ? void 0 : appbarConnectorService.setInteractiveFunctions(instance);
            // Register the app icon listener
            var appsIconElementRef = instance === null || instance === void 0 ? void 0 : instance.getAppIconElementRef(HubTileID.appgallery);
            registerAppBarAppsCallout((_a = appsIconElementRef === null || appsIconElementRef === void 0 ? void 0 : appsIconElementRef.current) !== null && _a !== void 0 ? _a : null);
            // Register the my content icon listener
            var myContentIconElementRef = instance === null || instance === void 0 ? void 0 : instance.getAppIconElementRef(HubTileID.mycontent);
            registerAppBarMyContentCallout((_b = myContentIconElementRef === null || myContentIconElementRef === void 0 ? void 0 : myContentIconElementRef.current) !== null && _b !== void 0 ? _b : null);
            // Register create app icon listener
            var createIconElementRef = instance === null || instance === void 0 ? void 0 : instance.getAppIconElementRef(HubTileID.create);
            registerAppBarCreateCallout((_c = createIconElementRef === null || createIconElementRef === void 0 ? void 0 : createIconElementRef.current) !== null && _c !== void 0 ? _c : null);
            // Register the feed icon listener
            var feedIconElementRef = instance === null || instance === void 0 ? void 0 : instance.getAppIconElementRef(HubTileID.feed);
            registerFeedCallout((_d = feedIconElementRef === null || feedIconElementRef === void 0 ? void 0 : feedIconElementRef.current) !== null && _d !== void 0 ? _d : null);
            // Register the flyout icon listener
            var flyoutButtonElementRef = instance === null || instance === void 0 ? void 0 : instance.getAppIconElementRef(FlyoutTileID);
            registerFlyoutCallout((_e = flyoutButtonElementRef === null || flyoutButtonElementRef === void 0 ? void 0 : flyoutButtonElementRef.current) !== null && _e !== void 0 ? _e : null);
            // Register OneDrive app icone listener
            var oneDriveIconElementRef = instance === null || instance === void 0 ? void 0 : instance.getAppIconElementRef(HubTileID.onedrive);
            registerAppBarOneDriveCallout((_f = oneDriveIconElementRef === null || oneDriveIconElementRef === void 0 ? void 0 : oneDriveIconElementRef.current) !== null && _f !== void 0 ? _f : null);
        }
    }, [
        appbarConnectorService,
        registerAppBarAppsCallout,
        registerAppBarMyContentCallout,
        registerAppBarCreateCallout,
        registerFeedCallout,
        registerFlyoutCallout,
        registerAppBarOneDriveCallout,
    ]);
    var featureGates = (_b = useFeatureGates()) === null || _b === void 0 ? void 0 : _b.data;
    var isVisualRefreshEnabled = (_e = (_d = (_c = featureGates === null || featureGates === void 0 ? void 0 : featureGates.result) === null || _c === void 0 ? void 0 : _c.visualRefreshGates) === null || _d === void 0 ? void 0 : _d.visualRefreshEnabled) !== null && _e !== void 0 ? _e : false;
    var props = __assign({ ref: appBarRefCallback, onAppTileHoverEnter: function () {
            return appbarConnectorService === null || appbarConnectorService === void 0 ? void 0 : appbarConnectorService.onAppButtonHoverEnter();
        }, onAppTileHoverLeave: function () {
            return appbarConnectorService === null || appbarConnectorService === void 0 ? void 0 : appbarConnectorService.onAppButtonHoverLeave();
        } }, (isVisualRefreshEnabled && { brandLogo: React.createElement(M365Logo, null) }));
    var AppBarRenderer = useRemotableRenderer(function () { return __awaiter(void 0, void 0, void 0, function () { return __generator(this, function (_a) {
        switch (_a.label) {
            case 0: return [4 /*yield*/, import('./RemotableAppBar')];
            case 1: return [2 /*return*/, (_a.sent()).RemotableAppBar];
        }
    }); }); }, AppBar);
    var NovaContextProviderRenderer = useRemotableRenderer(function () { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, import('@office-start/nova-context-provider')];
                case 1: return [2 /*return*/, (_a.sent())
                        .RemotableNovaContextProvider];
            }
        });
    }); }, NovaContextProvider);
    var _f = __read(useState(false), 2), isAIHubEnabled = _f[0], setIsAIHubEnabled = _f[1];
    useEffect(function () {
        fetchAIHubEnabled().then(function (enabled) { return setIsAIHubEnabled(enabled); });
    }, []);
    var isDarkMode = useIsDarkMode();
    return (React.createElement(OfficeFluentProvider, null, NovaContextProviderRenderer && (React.createElement(NovaContextProviderRenderer, { appbarConnectorService: appbarConnectorService },
        React.createElement("div", { "data-testid": "appbar", className: classNames(!isVisualRefreshEnabled && 'ms-bgc-nla', mergeClasses(styles['left-nav-container'], isVisualRefreshEnabled &&
                styles['left-nav-container-wide'], isDarkMode &&
                styles['left-nav-container-wide-dark'])) },
            isAIHubEnabled && (React.createElement(React.Fragment, null,
                React.createElement(LazyFeedbackComponent, null),
                React.createElement(LazyMeControlComponent, null))),
            AppBarRenderer && React.createElement(AppBarRenderer, __assign({}, props)))))));
};
//# sourceMappingURL=AppBarComponent.js.map